import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import Dropzone from 'react-dropzone';
import classnames from 'classnames';
import { setFlashMessage } from '@src/redux/actions/flashMessages';
import apolloClient from '@src/graphql/apollo';
import { ERROR_CODES } from '@src/constants';
import SectionTitle from '@src/components/SectionTitle';
import { UploadMediaDocument } from '@src/graphql/generated';
import './styles.scss';

const allowedFileTypes = 'image/jpeg,image/jpg,image/png';
const maxSizeInBytes = 10000000;

@connect(null, { setFlashMessage })
export default class MediaUpload extends PureComponent {
  static propTypes = {
    canDelete: PropTypes.bool,

    className: PropTypes.string,

    disabled: PropTypes.bool,

    // for single upload
    file: PropTypes.object,

    // multiple uploads
    files: PropTypes.array,

    height: PropTypes.number,

    invalid: PropTypes.bool,

    isCopyAndStatusOutsideDropZone: PropTypes.bool,

    keepFile: PropTypes.bool,

    mediaUploadCopy: PropTypes.node,

    mediaWrapperClassName: PropTypes.string,

    minHeight: PropTypes.number,

    minWidth: PropTypes.number,

    onRemove: PropTypes.func,

    // general
    onUpload: PropTypes.func,

    placeholder: PropTypes.node,

    // Redux actions
    setFlashMessage: PropTypes.func.isRequired,

    single: PropTypes.bool,

    singleMediaCopyWrapperClassName: PropTypes.string,

    title: PropTypes.string,

    titleSize: PropTypes.string,

    width: PropTypes.number,
  };

  static defaultProps = {
    canDelete: true,
    files: [],
  };

  state = {
    error: undefined,
    status: undefined,
  };

  onDrop = newfiles => {
    const { width, height, minWidth, minHeight } = this.props;

    return map(newfiles, newFile => this.upload(newFile, width || minWidth, height || minHeight));
  };

  validateFile = async file => {
    const { minWidth, minHeight } = this.props;

    return new Promise((resolve, reject) => {
      if (!minWidth && !minHeight) {
        return resolve(true);
      }

      const i = new Image();
      i.onload = () => {
        // eslint-disable-next-line no-undef
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          if (i.width < minWidth) {
            return reject(new Error(`Image width is less than ${minWidth} pixels`));
          }

          if (i.height < minHeight) {
            return reject(new Error(`Image height is less than ${minHeight} pixels`));
          }

          resolve();
        };
      };

      i.src = file.preview;
    });
  };

  upload = async (newfile, width = 512, height = 512) => {
    try {
      await this.validateFile(newfile);
    } catch (error) {
      if (this.props.file) {
        this.props.setFlashMessage({
          title: error.message,
          type: 'error',
        });
      } else {
        this.setState({ error: error.message });
      }

      return;
    }

    const { onUpload } = this.props;

    this.setState({
      error: '',
      status: 'uploading',
    });

    try {
      const {
        data: { uploadImage },
      } = await apolloClient.mutate({
        mutation: UploadMediaDocument,
        variables: { file: newfile, height, width },
      });

      Object.assign(uploadImage, { _id: uploadImage.id });
      onUpload && onUpload(uploadImage);

      window.URL.revokeObjectURL(newfile.preview);
      this.setState({ status: undefined });
    } catch (err) {
      if (this.props.file) {
        setFlashMessage({
          title: ERROR_CODES.file_upload_error,
          type: 'error',
        });
      } else {
        this.setState({
          error: ERROR_CODES.file_upload_error,
        });
      }
    }
  };

  remove = file => {
    const { onRemove, keepFile } = this.props;

    this.setState({ status: 'removing' });

    if (keepFile) {
      onRemove && onRemove(file);
    } else {
      onRemove && onRemove(file);
      this.setState({ status: undefined });
    }
  };

  onDropRejected = (...args) => {
    const fileName = args[0] && args[0][0] ? args[0][0].name : '';
    const error = this.fileValidationError(fileName);

    if (this.props.file) {
      setFlashMessage({
        title: error,
        type: 'error',
      });
    } else {
      this.setState({ error });
    }
  };

  fileValidationError = fileName => {
    const error = `Error uploading ${fileName || 'file'}. `;

    return `${error} ${ERROR_CODES.file_validation_error}`;
  };

  renderSingleImageUploadCopyAndStatus = ({ disabled, error, height, status, width }) => {
    const { mediaUploadCopy, singleMediaCopyWrapperClassName } = this.props;

    return (
      <div className={singleMediaCopyWrapperClassName}>
        {!status && !error && !disabled && (
          <div className="media-drag-single">
            {mediaUploadCopy || (
              <>
                <p>
                  {width}x{height}
                </p>
                <p>Drag a file here or</p>
                <p>click to upload</p>
              </>
            )}
          </div>
        )}

        {status === 'removing' && (
          <div className="media-drag-single">
            <p>Removing...</p>
          </div>
        )}

        {status === 'uploading' && (
          <div className="media-drag-single">
            <p>Uploading...</p>
          </div>
        )}
      </div>
    );
  };

  render() {
    const {
      placeholder,
      file,
      files,
      single,
      title,
      className,
      titleSize,
      width,
      height,
      disabled,
      invalid,
      mediaWrapperClassName,
      isCopyAndStatusOutsideDropZone,
      canDelete,
    } = this.props;
    const { status, error } = this.state;
    const errorClass = (error || invalid) && 'invalid';

    if (single) {
      return (
        <div className={classnames('media-upload', errorClass, className)}>
          {title && <SectionTitle size={titleSize} title={title} />}

          <Dropzone
            accept={allowedFileTypes}
            className={classnames('media-drag', !!file && 'file-uploaded', mediaWrapperClassName)}
            maxSize={maxSizeInBytes}
            onDrop={this.onDrop}
            onDropRejected={this.onDropRejected}
          >
            {!isCopyAndStatusOutsideDropZone &&
              this.renderSingleImageUploadCopyAndStatus({ disabled, error, height, status, width })}

            {!!file && (
              <div className="media-drag-single-preview">
                <img src={file.url} />
                {canDelete ? (
                  <span
                    className="media-preview-remove"
                    onClick={evt => {
                      evt.stopPropagation();
                      this.remove(file);
                    }}
                  >
                    x
                  </span>
                ) : null}
              </div>
            )}

            {error && <div>{error}</div>}
          </Dropzone>
          {isCopyAndStatusOutsideDropZone &&
            this.renderSingleImageUploadCopyAndStatus({ disabled, error, height, status, width })}
        </div>
      );
    }

    return (
      <div className={classnames('media-upload', errorClass, className)}>
        <SectionTitle size={titleSize} title={title} />

        <Dropzone
          accept={allowedFileTypes}
          className="media-drag"
          maxSize={maxSizeInBytes}
          multiple
          onDrop={this.onDrop}
          onDropRejected={this.onDropRejected}
        >
          {!error && (
            <div>
              <p>{placeholder}</p>
              <p>Drag image here or</p>
              <p>click to upload</p>
            </div>
          )}

          {error && <div>{error}</div>}
        </Dropzone>

        <ul className="media-preview">
          {files &&
            canDelete &&
            map(files, file => {
              if (file) {
                return (
                  <li className="media" key={file.id} style={{ backgroundImage: "url('" + file.url + "')" }}>
                    <span className="media-preview-remove" onClick={() => this.remove(file)}>
                      x
                    </span>
                  </li>
                );
              }
            })}

          {status === 'uploading' && (
            <li className="media blink" key="uploading">
              Uploading...
            </li>
          )}
        </ul>
      </div>
    );
  }
}
